import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  Ban,
  Copy,
  DollarSign,
  Download,
  Mail,
  Printer,
  Send,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import InvoiceSendModal from '../components/InvoiceSendModal';
import VoidConfirmationModal from '../components/VoidConfirmationModal';
import {
  getInvoiceById,
  postInvoice,
  voidInvoice,
  copyInvoice,
  downloadInvoicePdf,
} from '../utils/invoiceApi';
import { printSalesInvoice } from '../utils/printSalesInvoice';
import { canEditInvoice } from '../utils/invoiceEditability';
import { canCopyInvoice } from '../utils/salesTransactionPermissions';
import type { Invoice, InvoiceStatus } from '../types/invoice';
import { formatCurrency, formatPreviewQuantity } from '../utils/formatCurrency';
import { parseInventoryQty } from '../utils/negativeInventory';
import { toast } from '../utils/toast';
import { invoiceVoidCopy } from '../utils/voidConfirmationCopy';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const canManageInvoices = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteOrVoid = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const getStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const canReceivePayment = (status: InvoiceStatus) => status === 'OPEN' || status === 'PARTIAL';

const customerName = (invoice: Invoice) =>
  invoice.customer_snapshot.name || invoice.customer?.display_name || '—';

const SalesView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);

  const invoiceId = Number(id);

  const isPosted =
    invoice != null && invoice.status !== 'DRAFT' && invoice.status !== 'VOIDED';
  const hasBackorderLines =
    (invoice?.lines ?? []).some((line) => parseInventoryQty(line.backorder_qty) > 0);

  const fetchInvoice = async () => {
    if (!id || Number.isNaN(invoiceId)) {
      setError('Invalid invoice ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getInvoiceById(invoiceId);
      setInvoice(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const handlePost = async () => {
    if (!invoice || !canManageInvoices()) return;
    if (!window.confirm(`Post invoice ${invoice.invoice_number}? This will affect inventory and GL.`)) {
      return;
    }
    try {
      setActionLoading(true);
      await postInvoice(invoice.id);
      setInvoice(await getInvoiceById(invoice.id));
      toast.success(`Invoice ${invoice.invoice_number} posted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to post invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async () => {
    if (!invoice || !canDeleteOrVoid()) return;

    try {
      setVoiding(true);
      setVoidError(null);
      await voidInvoice(invoice.id);
      setInvoice(await getInvoiceById(invoice.id));
      setShowVoidModal(false);
      toast.success(`Invoice ${invoice.invoice_number} voided.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to void invoice';
      setVoidError(message);
      toast.error(message);
    } finally {
      setVoiding(false);
    }
  };

  const handleCopy = async () => {
    if (!invoice || !canCopyInvoice()) return;
    if (
      !window.confirm(
        `Copy invoice ${invoice.invoice_number}? A new draft will be created.`
      )
    ) {
      return;
    }
    try {
      setActionLoading(true);
      const copied = await copyInvoice(invoice.id);
      toast.success(`Copied to ${copied.invoice_number}.`);
      navigate(`/manage-sales?id=${copied.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to copy invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!invoice) return;
    try {
      setActionLoading(true);
      await printSalesInvoice(invoice);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to print invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    try {
      setActionLoading(true);
      await downloadInvoicePdf(invoice.id, `${invoice.invoice_number}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download invoice PDF');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
        </main>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-red-500 mb-3" />
            <p className="text-gray-900 font-medium mb-2">Could not load invoice</p>
            <p className="text-sm text-gray-500 mb-4">{error || 'Invoice not found.'}</p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={fetchInvoice}
                className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
              >
                Retry
              </button>
              <Link
                to="/sales"
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Back to Invoices
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-hidden">
        <div className="w-full max-w-full">
          <div className="mb-6">
            <Link
              to="/sales"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Invoices
            </Link>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                  {invoice.invoice_number}
                </h1>
                <span
                  className={`inline-flex mt-2 px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}
                >
                  {formatLabel(invoice.status)}
                </span>
                {invoice.allow_negative_stock && (
                  <span className="inline-flex mt-2 ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                    Oversell allowed
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm mb-6 max-w-xl">
              <h2 className="font-semibold text-gray-900 mb-3">Customer & Dates</h2>
              <div className="flex justify-between">
                <span className="text-gray-600">Customer</span>
                <span className="font-medium">{customerName(invoice)}</span>
              </div>
              {invoice.customer_snapshot.email && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Email</span>
                  <span>{invoice.customer_snapshot.email}</span>
                </div>
              )}
              {invoice.customer_snapshot.phone && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone</span>
                  <span>{invoice.customer_snapshot.phone}</span>
                </div>
              )}
              {invoice.customer_snapshot.address && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Address</span>
                  <span className="text-right max-w-[60%]">{invoice.customer_snapshot.address}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Invoice date</span>
                <span>{formatDate(invoice.txn_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Due date</span>
                <span>{formatDate(invoice.due_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Terms</span>
                <span>{formatLabel(invoice.payment_terms)}</span>
              </div>
            </div>

            <div className="mb-6 overflow-x-auto">
              <h2 className="font-semibold text-gray-900 mb-3">Line Items</h2>
              {hasBackorderLines && (
                <p className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  One or more lines were sold on backorder. COGS includes an estimate until stock is
                  received; amounts may change after a bill or adjustment clears the backorder.
                </p>
              )}
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Disc %</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                    {isPosted && hasBackorderLines && (
                      <>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Backorder
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Est. COGS
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          COGS
                        </th>
                      </>
                    )}
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Line Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(invoice.lines ?? []).map((line) => (
                    <tr key={line.id}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{line.item_snapshot.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{line.item_snapshot.sku}</div>
                        {line.description && (
                          <div className="text-xs text-gray-600 mt-1 whitespace-pre-line">{line.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">{formatPreviewQuantity(line.qty)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(line.unit_price)}</td>
                      <td className="px-4 py-2 text-right">{line.discount_percent}%</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(line.tax_amount)}</td>
                      {isPosted && hasBackorderLines && (
                        <>
                          <td className="px-4 py-2 text-right font-mono text-rose-700">
                            {parseInventoryQty(line.backorder_qty) > 0
                              ? formatPreviewQuantity(line.backorder_qty ?? '0')
                              : '—'}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {parseInventoryQty(line.backorder_qty) > 0
                              ? formatCurrency(line.estimated_cogs_amount ?? '0')
                              : '—'}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {parseInventoryQty(line.backorder_qty) > 0
                              ? formatCurrency(line.cogs_amount)
                              : '—'}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-2 text-right font-medium">{formatCurrency(line.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-6">
              <div className="w-full sm:w-80 bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(invoice.sub_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount</span>
                  <span className="font-medium">{formatCurrency(invoice.discount_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium">{formatCurrency(invoice.tax_total)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-gray-200 pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(invoice.total_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Balance</span>
                  <span
                    className={
                      parseFloat(invoice.balance) > 0
                        ? 'text-red-600 font-semibold'
                        : 'text-green-600 font-semibold'
                    }
                  >
                    {formatCurrency(invoice.balance)}
                  </span>
                </div>
              </div>
            </div>

            {invoice.memo && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Memo:</span> {invoice.memo}
              </p>
            )}
            {invoice.notes && (
              <p className="text-sm text-gray-500 mb-4">
                <span className="font-medium">Notes:</span> {invoice.notes}
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
              {canEditInvoice(invoice) && canManageInvoices() && (
                <button
                  type="button"
                  onClick={() => navigate(`/manage-sales?id=${invoice.id}`)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  {invoice.status === 'DRAFT' ? 'Edit Draft' : 'Amend Invoice'}
                </button>
              )}
              {canCopyInvoice() && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </button>
              )}
              {invoice.status === 'DRAFT' && canManageInvoices() && (
                <button
                  type="button"
                  onClick={handlePost}
                  className="flex items-center px-4 py-2 bg-green-700 text-white rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Post Invoice
                </button>
              )}
              {invoice.status === 'OPEN' && canDeleteOrVoid() && (
                <button
                  type="button"
                  onClick={openVoidModal}
                  className="flex items-center px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm"
                  disabled={actionLoading || voiding}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Void
                </button>
              )}
              {canReceivePayment(invoice.status) && (
                <button
                  type="button"
                  onClick={() =>
                    navigate('/receive-payments', {
                      state: { customerId: invoice.customer_id, invoiceId: invoice.id },
                    })
                  }
                  className="flex items-center px-4 py-2 bg-slate-800 text-white rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Receive Payment
                </button>
              )}
              <button
                type="button"
                onClick={() => void handlePrint()}
                className="flex items-center px-4 py-2 bg-slate-800 text-white rounded-lg text-sm"
                disabled={actionLoading}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </button>
              <button
                type="button"
                onClick={() => void handleDownloadPdf()}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm"
                disabled={actionLoading}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
              {canManageInvoices() && (
                <button
                  type="button"
                  onClick={() => setShowSendModal(true)}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send
                </button>
              )}
            </div>
          </div>
        </div>

        <InvoiceSendModal
          invoice={invoice}
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
        />

        <VoidConfirmationModal
          isOpen={showVoidModal}
          {...invoiceVoidCopy(invoice.invoice_number)}
          isSubmitting={voiding}
          error={voidError}
          onCancel={cancelVoidModal}
          onConfirm={confirmVoid}
        />
      </main>
    </div>
  );
};

export default SalesView;
